import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { Button } from '@/components/ui/Button/button';
import Toast from '@/components/ui/Toast/toast';
import { AppRoutes } from '@/i18n/constants';
import { getMarketFromPathname, getPathnameForMarket } from '@/libs/market/market';
import existingAccountTiger from '@/public/assets/image/existing-account/existing-account-tiger@3x.webp';
import { APIError } from '@/queries/httpClient';
import { useLogin } from '../../hooks/useLogin';

interface ExistingAccountStepProps {
  username: string;
  password: string;
  onCloseAction: () => Promise<void>;
}

export const ExistingAccountStep = ({ username, password, onCloseAction }: ExistingAccountStepProps) => {
  const t = useTranslations('existing-account-modal');
  const tSignIn = useTranslations('sign-in');
  const [loading, setLoading] = useState(false);
  const { login } = useLogin();

  const onClickPlayNow = async () => {
    try {
      setLoading(true);
      await login({ username, password });

      const homePath = getPathnameForMarket(AppRoutes.home, getMarketFromPathname(window.location.pathname));
      await onCloseAction();
      window.location.replace(homePath);
    } catch (error) {
      const isApiError = error instanceof APIError;

      if (isApiError && error.code === 'ACCOUNT_SUSPENDED') {
        Toast({ type: 'error', title: tSignIn('user-suspended') });
        return;
      }

      if (isApiError && error.code === 'EMAIL_NOT_VERIFIED') {
        Toast({ type: 'error', title: tSignIn('email-not-verified') });
        return;
      }

      Toast({ type: 'error', title: tSignIn('incorrect-email-or-password') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute left-0 top-0 isolate flex h-full w-full flex-col overflow-hidden">
      <div aria-hidden="true" className="absolute top-10.25 left-[calc(50%+5.375rem)] -z-1 w-124.25 h-61 -translate-x-1/2 -scale-y-100 rotate-165 rounded-full bg-frame-gradient blur-[91px] pointer-events-none lg:left-[calc(50%+4.75rem)]" />
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden hide-scrollbar">
        <Image src={existingAccountTiger} alt="" sizes="360px" priority className="mx-auto block h-auto w-90 max-w-none" />
        <div className="mt-3 flex flex-col gap-2 px-4 py-6 text-center text-white lg:px-6.5">
          <h2 className="text-xl font-medium leading-[1.3]">{t('title')}</h2>
          <p className="text-sm leading-5">{t('description')}</p>
        </div>
      </div>
      <div className="bg-linear-to-b from-c900/0 to-c900 to-35% px-4 pb-6 pt-4 lg:px-6">
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full px-6 py-3"
          onClick={onClickPlayNow}
          isLoading={loading}
          disabled={loading}
        >
          {t('play-now')}
        </Button>
      </div>
    </div>
  );
};
